import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import HelpMeDecideContent from 'components/common/helpMeDecide/HelpMeDecideContent';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { useQueryParams } from 'global/hooks/useQuery';
import DynamicFormModal from 'components/common/modals/DynamicFormModal';
import { createStyles } from 'components/common/helpMeDecide/commonCriteriaStyles';
import { INITIAL_DDWI_BC } from 'global/utils/HelpMeDecideHelper';
import DentalCriteria1 from '../GroupDental/Criteria1';
import DentalCriteria2 from '../GroupDental/Criteria2';
import DentalCriteria3 from '../GroupDental/Criteria3';

export interface HelpMeDecideDDIAProps {
  numberOfDentalCriteria: number;
}

const HelpMeDecideDDWI = observer(({ numberOfDentalCriteria }: HelpMeDecideDDIAProps): JSX.Element => {
  const classes = createStyles();
  const history = useHistory();
  //  Query params
  const query = useQueryParams();
  const cartId = query.get('cartId') || null;
  const pLineId = query.get('productLineId') || null;
  const groupId = query.get('groupId') || null;

  // Mobx store
  const {
    helpMeDecideStore: { criteriaIndex, setCriteriaIndex, setCurrentProductLine },
    browsingStore: {
      loading,
      currentProductLineId,
      cart,
      productLines,
      setCurrentProductLineId,
      collectInitialData,
      updateCart,
      setLoading,
    },
    browsingCriteriaModalStore: {
      openBrowsingCriteriaModal,
      closeBrowsingCriteriaModal,
      saveBrowsingCriteria,
      showBrowsingCriteriaModal,
      dynamicForm,
      form,
      loading: bcModalLoading,
    },
    browsingFilterStore: {
      browsingCriteria,
      setBrowsingCriteria,
      resetBcFromNextCriteria,
      onBrowsingCriteriaChange,
      getBrowsingCriteriaValue,
    },
    domainStore: { domainHeader },
  } = useRootStore();

  useEffect(() => {
    if (cartId && pLineId && groupId) {
      void collectInitialData(groupId, cartId, pLineId as ProductLineId);
      setCurrentProductLine(pLineId as ProductLineId);
      setBrowsingCriteria(INITIAL_DDWI_BC);
    }
  }, []);

  const convertedCart = toJS(cart);
  const convertedDomain = toJS(domainHeader);
  const convertedBc = toJS(browsingCriteria);
  const convertedProductLines = productLines.map((line) => toJS(line)).slice();

  const productLineIdFromUrl = (): ProductLineId => {
    return pLineId ? (pLineId as ProductLineId) : 'GroupDental';
  };

  const getNumberOfCriteria = (): number => {
    switch (pLineId) {
      case 'GroupDental':
        return numberOfDentalCriteria;
      default:
        return 0;
    }
  };

  const openBcModal = (): void => {
    const selectedProductLineId = productLineIdFromUrl();

    setCurrentProductLineId(selectedProductLineId);
    const pLine = convertedProductLines.find((line) => line.productLineId === selectedProductLineId);
    void openBrowsingCriteriaModal(
      convertedCart,
      selectedProductLineId,
      pLine?.eStoreBcFormId || pLine?.bcFormId || '',
      convertedBc,
    );
  };

  const saveModalChanges = async (currentBc: Record<string, string>): Promise<void> => {
    if (!currentProductLineId) throw new Error('Modal cannot be opened without currentProductLineId in the store');
    setLoading(true);
    const bc = { ...currentBc };
    if (bc.groupSize === '2-4') {
      bc.ortho = 'false';
      bc.orthoCoverageLevel = '';
    }
    if (bc.ortho === 'false') {
      bc.orthoCoverageLevel = '';
    }

    const updatedCart = await saveBrowsingCriteria(bc, pLineId ? (pLineId as ProductLineId) : null);
    runInAction(() => {
      updateCart(updatedCart);
      closeBrowsingCriteriaModal();
      setLoading(false);
      history.push(
        `/browsing?groupId=${groupId}&cartId=${cartId}&productLineId=${currentProductLineId}&cartType=browsing&backType=help-me-decide`,
      );
    });
  };

  const changeCriteria = (type: 'BACK' | 'NEXT'): void => {
    const isFirstCriteria = criteriaIndex === 1;
    const isLastCriteria = criteriaIndex === getNumberOfCriteria();

    switch (type) {
      case 'BACK':
        if (!isFirstCriteria) {
          setCriteriaIndex(criteriaIndex - 1);
        } else {
          setBrowsingCriteria(INITIAL_DDWI_BC);
          const url = `/popular-plans?groupId=${groupId}&productLineId=${pLineId}&cartId=${cartId}&cartType=popular`;
          history.push(url);
        }
        if (pLineId) resetBcFromNextCriteria(convertedDomain.id, criteriaIndex, pLineId as ProductLineId);
        break;
      case 'NEXT':
        if (isLastCriteria) {
          openBcModal();
        } else {
          setCriteriaIndex(criteriaIndex + 1);
        }
        break;
      default:
        history.goBack();
        break;
    }
  };

  const getCurrentCriteria = (productLineId: ProductLineId | null): JSX.Element => {
    // These props are used to render criteria form
    const commonCriteriaProps = {
      numberOfCriteria: getNumberOfCriteria(),
      minHeight: '53rem',
      onContinue: (): void => {
        changeCriteria('NEXT');
      },
      onBack: (): void => {
        changeCriteria('BACK');
      },
    };

    switch (productLineId) {
      case 'GroupDental':
        switch (criteriaIndex) {
          case 1:
            return <DentalCriteria1 {...commonCriteriaProps} />;
          case 2:
            return <DentalCriteria2 {...commonCriteriaProps} />;
          case 3:
            return <DentalCriteria3 {...commonCriteriaProps} />;
          default:
            return <></>;
        }
      default:
        return <></>;
    }
  };

  return (
    <>
      <HelpMeDecideContent formContent={getCurrentCriteria(pLineId ? (pLineId as ProductLineId) : null)} />
      <DynamicFormModal
        show={showBrowsingCriteriaModal}
        onClose={(): void => {
          // If BC modal wasn't submitted - set another value
          if (getBrowsingCriteriaValue('groupSize') === '2-4') {
            onBrowsingCriteriaChange('groupSize', INITIAL_DDWI_BC.groupSize || '');
          }
          if (getBrowsingCriteriaValue('ortho') === 'false') {
            onBrowsingCriteriaChange('ortho', INITIAL_DDWI_BC.ortho || '');
          }

          closeBrowsingCriteriaModal();
        }}
        onAction={(): void => {
          void saveModalChanges(convertedBc);
        }}
        actionButtonText="View Plans"
        dynamicForm={dynamicForm}
        form={form}
        loading={bcModalLoading || loading}
        className={classes.bcModal}
      />
    </>
  );
});

export default HelpMeDecideDDWI;
